import "../building-panels.css";
import { Icon as FunctionalIcon } from "../../ui/Icon.js";
import { buildingById } from "@zend/content";
import { AssetIcon } from "../../assets/Icon.js";
import { useT } from "../../i18n/index.js";
import { Button } from "../../ui/Button.js";
import { Timer, formatDuration, useServerTick } from "../../ui/Timer.js";
import { baseApi } from "../api.js";
import { useBase } from "../state.js";
import { rotated } from "../grid.js";
import { CostRow, Progress, Sheet, SpeedupList, Stat, attributeLabel, buildingDescription, buildingName, formatAttribute, instantPrice } from "./shared.js";
import { PayAction } from "./PayAction.js";
import { PurposePanel } from "./PurposePanels.js";
import { CollectionPanel } from "../ColonyPanels.js";
import { isServiceBuilding, ServiceBuildingPanel } from "./ServiceBuildingPanel.js";

export function BuildingPanel({ rowId }: { rowId: string }) {
  const t = useT();
  const base = useBase((s) => s.base);
  const run = useBase((s) => s.run);
  const setPanel = useBase((s) => s.setPanel);
  const setMode = useBase((s) => s.setMode);
  const setGhost = useBase((s) => s.setGhost);
  const now = useServerTick();
  const b = base?.buildings.find((x) => x.id === rowId);
  if (!base || !b) return null;
  const def = buildingById(b.buildingId);
  const isHq = b.buildingId === "bld.command_nexus";
  const running = b.state !== "idle" && b.finishesAt !== null;
  const price = running ? instantPrice(b.finishesAt!, now) : 0;

  const startMove = () => {
    if (b.w === 0) return;
    const fp = def.footprint === "ring" ? { w: 0, h: 0 } : rotated(def.footprint.w, def.footprint.h, b.rot);
    setMode({ kind: "move", rowId: b.id, w: fp.w, h: fp.h, rot: b.rot });
    setGhost({ x: b.x, y: b.y, valid: true });
    setPanel({ kind: "none" });
  };
  // Rotate in place reuses the move route: same cell, next quarter turn; the server validates the turned footprint (D65).
  const rotateInPlace = () => {
    if (b.w === 0) return;
    void run(() => baseApi.move(b.id, b.x, b.y, (b.rot + 90) % 360), t("base.toast.moved"));
  };
  const actionRow = b.w > 0 ? (
    <div className="row building-actions" style={{ gap: 8 }}>
      <Button className="service-move" onClick={startMove} data-testid="building-move"><FunctionalIcon name="move" /> {t("base.move")}</Button>
      <Button className="service-move" onClick={rotateInPlace} data-testid="building-rotate"><FunctionalIcon name="rotate" /> {t("base.rotateInPlace")}</Button>
    </div>
  ) : null;

  if (isServiceBuilding(b.buildingId)) return <Sheet title={buildingName(b.buildingId)} onClose={() => setPanel({ kind: "none" })} testId="building-panel" actions={actionRow}>
    <ServiceBuildingPanel id={b.buildingId} level={b.level} close={() => setPanel({ kind: "none" })} />
  </Sheet>;

  return (
    <Sheet title={buildingName(b.buildingId)} onClose={() => setPanel({ kind: "none" })} testId="building-panel" actions={actionRow}>
      <div className="building-hero" data-category={def.category}>
        <div className="building-hero-art"><AssetIcon id={b.buildingId} buildingLevel={b.level} size={160} alt={buildingName(b.buildingId)} /></div>
        <div className="building-hero-copy">
          <span className="building-category">{t(`cat.${def.category}`)}</span>
          <strong className="building-rank" data-testid="building-level">Lv {b.level}{running ? <> <FunctionalIcon name="arrowRight" /> {b.targetLevel}</> : null}</strong>
          <p>{buildingDescription(b.buildingId)}</p>
        </div>
      </div>

      {!running && b.next ? (
        <div className="section" data-testid="building-upgrade">
          <h3>{t("base.upgrade.to", { n: b.next.level })}</h3>
          <div className="row" style={{ flexWrap: "wrap", gap: 12 }}>
            <CostRow cost={b.next.cost} />
            <span className="mono dim"><FunctionalIcon name="clock" /> {formatDuration(b.next.timeMs)}</span>
          </div>
          {base.queues.build.used >= base.queues.build.slots ? <Button variant="gold" onClick={() => setPanel({ kind: "queue" })} data-testid="building-hire-builder">{t(base.queues.build.permanent >= 3 ? "base.queue.title" : "base.queue.hireTitle")}</Button> : null}
          <PayAction cost={b.next.cost} reasons={b.canUpgrade.reasons} onStart={(cover) => run(() => baseApi.upgrade(b.id, cover), t("base.toast.upgradeStarted"))} label={<><FunctionalIcon name="arrowUp" /> {t("base.upgrade")}</>} testId="building-upgrade-btn" />
        </div>
      ) : null}

      {running ? (
        <div className="section" data-testid="building-running">
          <div className="row" style={{ marginBottom: 6 }}>
            <span className="grow">{b.state === "constructing" ? t("base.state.constructing") : t("base.state.upgrading", { n: b.targetLevel })}</span>
            <Timer finishesAt={b.finishesAt!} className="timer" />
          </div>
          <Progress startedAt={b.startedAt} finishesAt={b.finishesAt!} />
          <h3>{t("base.speedup.title")}</h3>
          <SpeedupList scope="build" onUse={(itemId, qty) => void run(() => baseApi.speedup(b.id, itemId, qty), t("base.toast.speedup"))} />
          <div className="row" style={{ marginTop: 12, gap: 8, flexWrap: "wrap" }}>
            <Button variant="gold" onClick={() => void run(() => baseApi.instant(b.id), t("base.toast.instant"))} data-testid="building-instant">
              <FunctionalIcon name="skip" /> {t("base.instant", { n: price })}
            </Button>
            <Button variant="danger" onClick={() => void run(() => baseApi.cancel(b.id), t("base.toast.cancelled"))} data-testid="building-cancel">
              {t("base.cancel")}
            </Button>
          </div>
        </div>
      ) : null}

      {Object.keys(b.attributes).length > 0 ? (
        <details className="section building-details">
          <summary>{t("base.attributes")}</summary>
          <div className="stats">
            {Object.entries(b.attributes).map(([k, v]) => (
              <Stat key={k} label={attributeLabel(k)} value={formatAttribute(k, v)} next={!running && b.nextAttributes ? formatAttribute(k, b.nextAttributes[k] ?? v) : null} />
            ))}
          </div>
        </details>
      ) : null}

      {isHq ? (
        <div className="section" data-testid="hq-gate">
          <h3>{t("base.gate.title")}</h3>
          <div className="row">
            <span className="grow">{t("base.gate.summary", { avg: base.gate.average.toFixed(2), req: base.gate.required })}</span>
            <span className={`badge ${base.gate.ok ? "ok" : "warn"}`}>{base.gate.maxed ? t("base.gate.maxed") : base.gate.ok ? t("base.gate.ok") : t("base.gate.blocked", { n: base.gate.drags.length })}</span>
          </div>
          <div className="row" style={{ marginTop: 8, gap: 8, flexWrap: "wrap" }}>
            <Button onClick={() => setPanel({ kind: "hq" })} data-testid="open-hq-panel">
              {t("base.hq.open")}
            </Button>
            <Button onClick={() => setPanel({ kind: "protocols" })}>{t("base.protocols.title")}</Button>
            <Button onClick={() => setPanel({ kind: "layouts" })}>{t("base.layouts.title")}</Button>
          </div>
        </div>
      ) : null}

      <PurposePanel panel={def.panel} level={b.level} />
      <CollectionPanel rowId={rowId} />

      {!running && !b.next && b.level >= 30 ? <p className="hint">{t("base.reason.max")}</p> : null}
    </Sheet>
  );
}
